import { useEffect, useRef, useState } from 'react';
import { Download, X } from 'lucide-react';
import { fetchExportStatus, requestExport } from '../api/exports';
import type { ExportResource } from '../api/exports';
import { Button } from './ui/button';

// One-shot async export control: click → enqueue → poll → Download link.
// The job runs through gl_jobs (1-min runner cadence), so "Preparing…" can
// take up to ~1–2 min; polling stops on done/failed or after MAX_POLL_MS.
const POLL_MS = 3000;
const MAX_POLL_MS = 5 * 60 * 1000;

type Phase = 'idle' | 'preparing' | 'ready' | 'failed';

export default function ExportButton({
  resource,
  filters,
  format = 'xlsx',
}: {
  resource: ExportResource;
  /** The view's currently applied filters — same keys as the list endpoint. */
  filters: Record<string, unknown>;
  /** What the worker produces for this resource — label only, the backend decides. */
  format?: 'xlsx' | 'csv';
}) {
  const formatLabel = format.toUpperCase();
  const [phase, setPhase] = useState<Phase>('idle');
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => () => { if (timer.current) clearInterval(timer.current); }, []);

  async function start() {
    setPhase('preparing');
    setUrl(null);
    setError(null);
    try {
      const jobId = await requestExport(resource, filters);
      const startedAt = Date.now();
      timer.current = setInterval(() => {
        void (async () => {
          try {
            const status = await fetchExportStatus(jobId);
            if (status.result_url) {
              stopPolling();
              setUrl(status.result_url);
              setPhase('ready');
            } else if (status.done) {
              stopPolling();
              setError(status.error ?? `Export ${status.status}`);
              setPhase('failed');
            } else if (Date.now() - startedAt > MAX_POLL_MS) {
              stopPolling();
              setError('Export is taking longer than expected — check Admin → Job Queue.');
              setPhase('failed');
            }
          } catch {
            // transient poll error — keep trying until MAX_POLL_MS
          }
        })();
      }, POLL_MS);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Export request failed');
      setPhase('failed');
    }
  }

  function stopPolling() {
    if (timer.current) {
      clearInterval(timer.current);
      timer.current = null;
    }
  }

  if (phase === 'ready' && url) {
    return (
      <span className="flex items-center gap-1">
        <Button asChild>
          <a href={url} target="_blank" rel="noopener noreferrer">
            <Download className="h-3.5 w-3.5" />
            Download {formatLabel}
          </a>
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => { setPhase('idle'); setUrl(null); }}
          title="New export"
          aria-label="New export"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </span>
    );
  }

  return (
    <span className="flex items-center gap-2">
      <Button
        variant="outline"
        onClick={() => void start()}
        disabled={phase === 'preparing'}
        title={`Export the current view (with active filters) to ${formatLabel}`}
      >
        <Download className="h-3.5 w-3.5" />
        {phase === 'preparing' ? 'Preparing…' : 'Export'}
      </Button>
      {phase === 'failed' && error && (
        <span className="max-w-[280px] truncate text-xs text-red-600" title={error}>
          {error}
        </span>
      )}
    </span>
  );
}
